// Admin Dashboard Page - Complete admin interface for managing vendedores, partners, clientes, and comissões
import { createElement } from '@/utils/dom'
import { authService } from '@/services/auth'
import { router } from '@/utils/router'
import {
    adminApi,
    type VendedorResumo,
    type PartnerResumo,
    type ClienteInfo,
    type Comissao,
    type ComissaoAPagar,
    type ComissaoSummaryResponse,
    type CreateVendedorData,
    type UpdateVendedorData,
    type CreatePartnerData,
    type UpdatePartnerData,
    type Vendedor
} from '@/services/admin'

type AdminTab = 'vendedores' | 'partners' | 'clientes' | 'todos_clientes' | 'comissoes'

export class AdminPage {
    private currentTab: AdminTab = 'vendedores'

    // Data
    private vendedores: VendedorResumo[] = []
    private partners: PartnerResumo[] = []
    private clientes: ClienteInfo[] = []
    private allClientes: ClienteInfo[] = []
    private comissoes: Comissao[] = []
    private comissoesSummary: ComissaoSummaryResponse | null = null
    private comissoesAPagar: ComissaoAPagar[] = []

    // For clientes filtering
    private clienteFilter = {
        plan: ''
    }

    // For comissoes filtering
    private comissaoFilter = {
        mes: '',
        tipo: '',
        estado: ''
    }

    // Selected comissoes for batch operations
    private selectedComissoes: Set<string> = new Set()

    async render(): Promise<HTMLElement> {
        const user = authService.getUser()

        // Check if user is admin
        if (!user || user.role !== 'admin') {
            router.navigate('/dashboard')
            return createElement('div', {
                className: 'admin-redirect',
                innerHTML: '<p>Acesso negado. A redirecionar...</p>'
            })
        }

        const container = createElement('div', { className: 'admin-page' })
        container.innerHTML = this.createLoadingState()

        // Load initial data
        await this.loadData()

        // Render full page
        container.innerHTML = this.createPageContent()
        this.addEventListeners(container)

        return container
    }

    private createLoadingState(): string {
        return `
            <div class="admin-loading">
                <div class="loading-spinner"></div>
                <p>A carregar painel de administração...</p>
            </div>
        `
    }

    private async loadData(): Promise<void> {
        try {
            // Load data based on current tab
            switch (this.currentTab) {
                case 'vendedores':
                    const vendedoresRes = await adminApi.listVendedores()
                    this.vendedores = vendedoresRes.vendedores
                    break
                case 'partners':
                    const partnersRes = await adminApi.listPartners()
                    this.partners = partnersRes.partners
                    // Also load vendedores for the dropdown
                    const vendedoresForDropdown = await adminApi.listVendedores()
                    this.vendedores = vendedoresForDropdown.vendedores
                    break
                case 'clientes':
                    const clientesRes = await adminApi.listClientesDiretos()
                    this.clientes = clientesRes.clientes
                    // Also load vendedores for assignment
                    const vendedoresForAssign = await adminApi.listVendedores()
                    this.vendedores = vendedoresForAssign.vendedores
                    break
                case 'todos_clientes':
                    const allClientesRes = await adminApi.listAllClientes(
                        this.clienteFilter.plan ? { plan: this.clienteFilter.plan } : undefined
                    )
                    this.allClientes = allClientesRes.clientes
                    break
                case 'comissoes':
                    const [comissoesRes, summaryRes, apagarRes] = await Promise.all([
                        adminApi.listComissoes(this.comissaoFilter),
                        adminApi.getComissoesSummary(),
                        adminApi.getComissoesAPagar()
                    ])
                    this.comissoes = comissoesRes.comissoes
                    this.comissoesSummary = summaryRes
                    this.comissoesAPagar = apagarRes
                    break
            }
        } catch (error) {
            console.error('Failed to load admin data:', error)
        }
    }

    private createPageContent(): string {
        return `
            <div class="admin-container">
                <!-- Header -->
                <header class="admin-header">
                    <div class="admin-header-left">
                        <button class="btn btn-ghost btn-back" id="admin-back">
                            <i class="fa-solid fa-arrow-left"></i>
                            <span>Voltar</span>
                        </button>
                        <h1 class="admin-title">
                            <i class="fa-solid fa-shield-halved"></i>
                            Painel de Administração
                        </h1>
                    </div>
                    <div class="admin-header-right">
                        <button class="btn btn-ghost btn-refresh" id="admin-refresh" title="Atualizar dados">
                            <i class="fa-solid fa-sync-alt"></i>
                            <span>Atualizar</span>
                        </button>
                        <span class="admin-user">
                            <i class="fa-solid fa-user-shield"></i>
                            ${this.escapeHtml(authService.getUser()?.name || 'Admin')}
                        </span>
                    </div>
                </header>

                <!-- Navigation Tabs -->
                <nav class="admin-tabs">
                    <button class="admin-tab ${this.currentTab === 'vendedores' ? 'active' : ''}" data-tab="vendedores">
                        <i class="fa-solid fa-user-tie"></i>
                        <span>Vendedores</span>
                    </button>
                    <button class="admin-tab ${this.currentTab === 'partners' ? 'active' : ''}" data-tab="partners">
                        <i class="fa-solid fa-handshake"></i>
                        <span>Parceiros</span>
                    </button>
                    <button class="admin-tab ${this.currentTab === 'todos_clientes' ? 'active' : ''}" data-tab="todos_clientes">
                        <i class="fa-solid fa-address-book"></i>
                        <span>Clientes</span>
                    </button>
                    <button class="admin-tab ${this.currentTab === 'clientes' ? 'active' : ''}" data-tab="clientes">
                        <i class="fa-solid fa-users"></i>
                        <span>Clientes Diretos</span>
                    </button>
                    <button class="admin-tab ${this.currentTab === 'comissoes' ? 'active' : ''}" data-tab="comissoes">
                        <i class="fa-solid fa-coins"></i>
                        <span>Comissões</span>
                    </button>
                </nav>

                <!-- Content Area -->
                <main class="admin-content">
                    ${this.renderTabContent()}
                </main>
            </div>
        `
    }

    private renderTabContent(): string {
        switch (this.currentTab) {
            case 'vendedores':
                return this.renderVendedoresSection()
            case 'partners':
                return this.renderPartnersSection()
            case 'clientes':
                return this.renderClientesSection()
            case 'todos_clientes':
                return this.renderTodosClientesSection()
            case 'comissoes':
                return this.renderComissoesSection()
            default:
                return '<p>Selecione uma secção</p>'
        }
    }

    // =========================================================================
    // VENDEDORES SECTION
    // =========================================================================

    private renderVendedoresSection(): string {
        return `
            <div class="admin-section vendedores-section">
                <div class="section-header">
                    <h2>Gestão de Vendedores</h2>
                    <button class="btn btn-primary" id="add-vendedor">
                        <i class="fa-solid fa-plus"></i>
                        Adicionar Vendedor
                    </button>
                </div>
                
                ${this.vendedores.length === 0 ? this.renderEmptyState('vendedores') : `
                    <div class="admin-table-container">
                        <table class="admin-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Email</th>
                                    <th>Partners</th>
                                    <th>Clientes</th>
                                    <th>Pendente (€)</th>
                                    <th>Estado</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.vendedores.map(v => this.renderVendedorRow(v)).join('')}
                            </tbody>
                        </table>
                    </div>
                `}
            </div>
        `
    }

    private renderVendedorRow(vendedor: VendedorResumo): string {
        return `
            <tr data-id="${vendedor.id}">
                <td>
                    <div class="entity-name">
                        <i class="fa-solid fa-user-tie entity-icon"></i>
                        <span>${this.escapeHtml(vendedor.nome)}</span>
                    </div>
                </td>
                <td>${this.escapeHtml(vendedor.email)}</td>
                <td class="text-center">
                    <span class="badge badge-info">${vendedor.total_partners || 0}</span>
                </td>
                <td class="text-center">
                    <span class="badge badge-secondary">${vendedor.total_clientes || 0}</span>
                </td>
                <td class="text-right">
                    <span class="money ${(vendedor.comissao_pendente || 0) > 0 ? 'pending' : ''}">
                        ${this.formatCurrency(vendedor.comissao_pendente || 0)}
                    </span>
                </td>
                <td>
                    <span class="status-badge ${vendedor.ativo ? 'active' : 'inactive'}">
                        ${vendedor.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                </td>
                <td>
                    <div class="action-buttons">
                        <button class="btn btn-icon btn-ghost" data-action="edit-vendedor" data-id="${vendedor.id}" title="Editar">
                            <i class="fa-solid fa-edit"></i>
                        </button>
                        <button class="btn btn-icon btn-ghost" data-action="view-vendedor-partners" data-id="${vendedor.id}" title="Ver Partners">
                            <i class="fa-solid fa-handshake"></i>
                        </button>
                        <button class="btn btn-icon btn-ghost btn-danger" data-action="delete-vendedor" data-id="${vendedor.id}" title="Desativar">
                            <i class="fa-solid fa-trash"></i>
                        </button>
                    </div>
                </td>
            </tr>
        `
    }

    // =========================================================================
    // PARTNERS SECTION
    // =========================================================================

    private renderPartnersSection(): string {
        return `
            <div class="admin-section partners-section">
                <div class="section-header">
                    <h2>Gestão de Parceiros</h2>
                    <button class="btn btn-primary" id="add-partner">
                        <i class="fa-solid fa-plus"></i>
                        Adicionar Parceiro
                    </button>
                </div>
                
                ${this.partners.length === 0 ? this.renderEmptyState('parceiros') : `
                    <div class="admin-table-container">
                        <table class="admin-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Vendedor</th>
                                    <th>Clientes</th>
                                    <th>Ativos</th>
                                    <th>Pendente (€)</th>
                                    <th>Estado</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.partners.map(p => this.renderPartnerRow(p)).join('')}
                            </tbody>
                        </table>
                    </div>
                `}
            </div>
        `
    }

    private renderPartnerRow(partner: PartnerResumo): string {
        return `
            <tr data-id="${partner.id}">
                <td>
                    <div class="entity-name">
                        <i class="fa-solid fa-handshake entity-icon"></i>
                        <span>${this.escapeHtml(partner.nome)}</span>
                    </div>
                </td>
                <td>
                    ${partner.vendedor_nome
                ? `<span class="vendedor-link">${this.escapeHtml(partner.vendedor_nome)}</span>`
                : '<span class="text-muted">N/A</span>'}
                </td>
                <td class="text-center">
                    <span class="badge badge-info">${partner.total_clientes || 0}</span>
                </td>
                <td class="text-center">
                    <span class="badge badge-success">${partner.clientes_ativos || 0}</span>
                </td>
                <td class="text-right">
                    <span class="money ${(partner.comissao_pendente || 0) > 0 ? 'pending' : ''}">
                        ${this.formatCurrency(partner.comissao_pendente || 0)}
                    </span>
                </td>
                <td>
                    <span class="status-badge ${partner.ativo ? 'active' : 'inactive'}">
                        ${partner.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                </td>
                <td>
                    <div class="action-buttons">
                        <button class="btn btn-icon btn-ghost" data-action="edit-partner" data-id="${partner.id}" title="Editar">
                            <i class="fa-solid fa-edit"></i>
                        </button>
                        <button class="btn btn-icon btn-ghost" data-action="view-partner-clientes" data-id="${partner.id}" title="Ver Clientes">
                            <i class="fa-solid fa-users"></i>
                        </button>
                        <button class="btn btn-icon btn-ghost btn-danger" data-action="delete-partner" data-id="${partner.id}" title="Desativar">
                            <i class="fa-solid fa-trash"></i>
                        </button>
                    </div>
                </td>
            </tr>
        `
    }

    // =========================================================================
    // CLIENTES DIRETOS SECTION
    // =========================================================================

    private renderClientesSection(): string {
        return `
            <div class="admin-section clientes-section">
                <div class="section-header">
                    <h2>Clientes Diretos (Sem Parceiro)</h2>
                    <div class="section-info">
                        <span class="badge badge-secondary">${this.clientes.length} clientes</span>
                    </div>
                </div>
                
                ${this.clientes.length === 0 ? this.renderEmptyState('clientes diretos') : `
                    <div class="admin-table-container">
                        <table class="admin-table">
                            <thead>
                                <tr>
                                    <th>Empresa</th>
                                    <th>Email</th>
                                    <th>Plano</th>
                                    <th>Vendedor</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.clientes.map(c => this.renderClienteRow(c)).join('')}
                            </tbody>
                        </table>
                    </div>
                `}
            </div>
        `
    }

    private renderClienteRow(cliente: ClienteInfo): string {
        const planLabel = this.getPlanLabel(cliente.plan_id)
        const statusClass = this.getSubscriptionStatusClass(cliente.subscription_status)

        return `
            <tr data-id="${cliente.user_id}">
                <td>
                    <div class="entity-name">
                        <i class="fa-solid fa-building entity-icon"></i>
                        <div class="entity-details">
                            <span class="entity-title">${this.escapeHtml(cliente.company_name || cliente.name)}</span>
                            <span class="entity-subtitle">NIF: ${this.escapeHtml(cliente.nif)}</span>
                        </div>
                    </div>
                </td>
                <td>${cliente.email ? this.escapeHtml(cliente.email) : '<span class="text-muted">N/A</span>'}</td>
                <td>
                    <span class="plan-badge ${statusClass}">${planLabel}</span>
                </td>
                <td>
                    ${cliente.vendedor_nome
                ? `<span class="vendedor-assigned">${this.escapeHtml(cliente.vendedor_nome)}</span>`
                : '<span class="text-muted">Não atribuído</span>'}
                </td>
                <td>
                    <div class="action-buttons">
                        <button class="btn btn-sm btn-outline" data-action="assign-vendedor" data-id="${cliente.user_id}" title="Atribuir Vendedor">
                            <i class="fa-solid fa-user-plus"></i>
                            <span>Atribuir</span>
                        </button>
                    </div>
                </td>
            </tr>
        `
    }

    // =========================================================================
    // TODOS CLIENTES SECTION (ALL CLIENTS)
    // =========================================================================

    private renderTodosClientesSection(): string {
        return `
            <div class="admin-section todos-clientes-section">
                <div class="section-header">
                    <h2>Gestão de Clientes</h2>
                    <div class="section-info">
                        <span class="badge badge-secondary">${this.allClientes.length} clientes</span>
                    </div>
                </div>

                <!-- Plan Filter -->
                <div class="clientes-filters">
                    <div class="filter-group">
                        <label>Plano</label>
                        <select id="filter-plan" class="filter-input">
                            <option value="">Todos</option>
                            <option value="free" ${this.clienteFilter.plan === 'free' ? 'selected' : ''}>Free</option>
                            <option value="solo" ${this.clienteFilter.plan === 'solo' ? 'selected' : ''}>Solo</option>
                            <option value="empresas" ${this.clienteFilter.plan === 'empresas' ? 'selected' : ''}>Empresas</option>
                            <option value="premium" ${this.clienteFilter.plan === 'premium' ? 'selected' : ''}>Ultra Premium</option>
                        </select>
                    </div>
                    <button class="btn btn-secondary" id="apply-cliente-filters">
                        <i class="fa-solid fa-filter"></i>
                        Aplicar
                    </button>
                </div>
                
                ${this.allClientes.length === 0 ? this.renderEmptyState('clientes') : `
                    <div class="admin-table-container">
                        <table class="admin-table clientes-all-table">
                            <thead>
                                <tr>
                                    <th>Nome / Empresa</th>
                                    <th>NIF</th>
                                    <th>Email</th>
                                    <th>Plano</th>
                                    <th>Renovação</th>
                                    <th>Registado há</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.allClientes.map(c => this.renderAllClienteRow(c)).join('')}
                            </tbody>
                        </table>
                    </div>
                `}
            </div>
        `
    }

    private renderAllClienteRow(cliente: ClienteInfo): string {
        const planLabel = this.getPlanLabel(cliente.plan_id)
        const statusClass = this.getSubscriptionStatusClass(cliente.subscription_status)
        const renewalInfo = this.formatRenewalDate(cliente.subscription_status, cliente.subscription_end_date)
        const timeSinceRegistration = this.formatTimeSinceRegistration(cliente.created_at)

        return `
            <tr data-id="${cliente.user_id}">
                <td>
                    <div class="entity-name">
                        <i class="fa-solid fa-building entity-icon"></i>
                        <div class="entity-details">
                            <span class="entity-title">${this.escapeHtml(cliente.company_name || cliente.name)}</span>
                            ${cliente.partner_nome ? `<span class="entity-subtitle">Parceiro: ${this.escapeHtml(cliente.partner_nome)}</span>` : '<span class="entity-subtitle text-muted">Cliente Direto</span>'}
                        </div>
                    </div>
                </td>
                <td>
                    <span class="nif-badge">${this.escapeHtml(cliente.nif)}</span>
                </td>
                <td>${cliente.email ? this.escapeHtml(cliente.email) : '<span class="text-muted">N/A</span>'}</td>
                <td>
                    <span class="plan-badge ${statusClass}">${planLabel}</span>
                </td>
                <td>
                    <span class="renewal-info ${renewalInfo.class}">${renewalInfo.text}</span>
                </td>
                <td>
                    <span class="registration-time">${timeSinceRegistration}</span>
                </td>
            </tr>
        `
    }

    private formatRenewalDate(status: string | null, endDate: string | null): { text: string; class: string } {
        // If no active subscription, show "Free"
        if (!status || status !== 'active') {
            return { text: 'N/A', class: 'free' }
        }

        // Parse the end date and show when it renews
        if (endDate) {
            try {
                const renewDate = new Date(endDate)
                const now = new Date()

                // Check if it's a valid date
                if (!isNaN(renewDate.getTime())) {
                    const daysUntilRenewal = Math.ceil((renewDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

                    if (daysUntilRenewal < 0) {
                        return { text: 'Expirado', class: 'expired' }
                    } else if (daysUntilRenewal === 0) {
                        return { text: 'Hoje', class: 'today' }
                    } else if (daysUntilRenewal <= 7) {
                        return { text: `${daysUntilRenewal}d`, class: 'soon' }
                    } else {
                        // Format as DD/MM/YYYY
                        const formatted = renewDate.toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })
                        return { text: formatted, class: 'normal' }
                    }
                }
            } catch {
                // Fall through to default
            }
        }

        return { text: 'Ativo', class: 'active' }
    }

    private formatTimeSinceRegistration(createdAt: string): string {
        try {
            const created = new Date(createdAt)
            const now = new Date()

            if (isNaN(created.getTime())) {
                return 'N/A'
            }

            const diffMs = now.getTime() - created.getTime()
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

            if (diffDays === 0) {
                return 'Hoje'
            } else if (diffDays === 1) {
                return '1 dia'
            } else if (diffDays < 30) {
                return `${diffDays} dias`
            } else if (diffDays < 365) {
                const months = Math.floor(diffDays / 30)
                return months === 1 ? '1 mês' : `${months} meses`
            } else {
                const years = Math.floor(diffDays / 365)
                const remainingMonths = Math.floor((diffDays % 365) / 30)
                if (remainingMonths === 0) {
                    return years === 1 ? '1 ano' : `${years} anos`
                } else {
                    return years === 1 ? `1 ano, ${remainingMonths}m` : `${years} anos, ${remainingMonths}m`
                }
            }
        } catch {
            return 'N/A'
        }
    }

    // =========================================================================
    // COMISSOES SECTION
    // =========================================================================

    private renderComissoesSection(): string {
        return `
            <div class="admin-section comissoes-section">
                <!-- Summary Cards -->
                <div class="comissoes-summary">
                    <div class="summary-card pending">
                        <div class="summary-icon">
                            <i class="fa-solid fa-clock"></i>
                        </div>
                        <div class="summary-content">
                            <span class="summary-value">${this.formatCurrency(this.comissoesSummary?.total_pendente || 0)}</span>
                            <span class="summary-label">Total Pendente</span>
                        </div>
                    </div>
                    <div class="summary-card paid">
                        <div class="summary-icon">
                            <i class="fa-solid fa-check-circle"></i>
                        </div>
                        <div class="summary-content">
                            <span class="summary-value">${this.formatCurrency(this.comissoesSummary?.total_pago || 0)}</span>
                            <span class="summary-label">Total Pago</span>
                        </div>
                    </div>
                    <div class="summary-card total">
                        <div class="summary-icon">
                            <i class="fa-solid fa-coins"></i>
                        </div>
                        <div class="summary-content">
                            <span class="summary-value">${this.formatCurrency(this.comissoesSummary?.total_mes || 0)}</span>
                            <span class="summary-label">Total Geral</span>
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="comissoes-filters">
                    <div class="filter-group">
                        <label>Mês</label>
                        <input type="month" id="filter-mes" value="${this.comissaoFilter.mes}" class="filter-input">
                    </div>
                    <div class="filter-group">
                        <label>Tipo</label>
                        <select id="filter-tipo" class="filter-input">
                            <option value="">Todos</option>
                            <option value="vendedor" ${this.comissaoFilter.tipo === 'vendedor' ? 'selected' : ''}>Vendedor</option>
                            <option value="partner" ${this.comissaoFilter.tipo === 'partner' ? 'selected' : ''}>Parceiro</option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label>Estado</label>
                        <select id="filter-estado" class="filter-input">
                            <option value="">Todos</option>
                            <option value="pendente" ${this.comissaoFilter.estado === 'pendente' ? 'selected' : ''}>Pendente</option>
                            <option value="pago" ${this.comissaoFilter.estado === 'pago' ? 'selected' : ''}>Pago</option>
                        </select>
                    </div>
                    <button class="btn btn-secondary" id="apply-filters">
                        <i class="fa-solid fa-filter"></i>
                        Aplicar
                    </button>
                </div>

                <!-- Actions Bar -->
                <div class="comissoes-actions">
                    <div class="selected-info">
                        <span id="selected-count">${this.selectedComissoes.size} selecionadas</span>
                    </div>
                    <div class="action-buttons">
                        <button class="btn btn-success" id="mark-paid" ${this.selectedComissoes.size === 0 ? 'disabled' : ''}>
                            <i class="fa-solid fa-check"></i>
                            Marcar como Pago
                        </button>
                        <button class="btn btn-outline" id="export-comissoes">
                            <i class="fa-solid fa-download"></i>
                            Exportar
                        </button>
                    </div>
                </div>

                <!-- Pending Payments Summary -->
                ${this.comissoesAPagar.length > 0 ? `
                    <div class="pending-payments-section">
                        <h3>Pagamentos Pendentes por Beneficiário</h3>
                        <div class="pending-payments-grid">
                            ${this.comissoesAPagar.map(c => this.renderPendingPaymentCard(c)).join('')}
                        </div>
                    </div>
                ` : ''}

                <!-- Comissoes Table -->
                ${this.comissoes.length === 0 ? this.renderEmptyState('comissões') : `
                    <div class="admin-table-container">
                        <table class="admin-table comissoes-table">
                            <thead>
                                <tr>
                                    <th class="checkbox-col">
                                        <input type="checkbox" id="select-all-comissoes">
                                    </th>
                                    <th>Beneficiário</th>
                                    <th>Tipo</th>
                                    <th>Mês</th>
                                    <th>Valor Sub.</th>
                                    <th>%</th>
                                    <th>Comissão</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.comissoes.map(c => this.renderComissaoRow(c)).join('')}
                            </tbody>
                        </table>
                    </div>
                `}
            </div>
        `
    }

    private renderPendingPaymentCard(payment: ComissaoAPagar): string {
        return `
            <div class="pending-payment-card">
                <div class="payment-header">
                    <span class="payment-type ${payment.tipo}">
                        <i class="fa-solid ${payment.tipo === 'vendedor' ? 'fa-user-tie' : 'fa-handshake'}"></i>
                        ${payment.tipo === 'vendedor' ? 'Vendedor' : 'Parceiro'}
                    </span>
                    <span class="payment-count">${payment.count} comissões</span>
                </div>
                <div class="payment-name">${this.escapeHtml(payment.nome || 'Desconhecido')}</div>
                ${payment.nif ? `<div class="payment-nif">NIF: ${this.escapeHtml(payment.nif)}</div>` : ''}
                ${payment.iban ? `<div class="payment-iban">IBAN: ${this.escapeHtml(payment.iban)}</div>` : ''}
                <div class="payment-total">${this.formatCurrency(payment.total_pendente)}</div>
            </div>
        `
    }

    private renderComissaoRow(comissao: Comissao): string {
        const isSelected = this.selectedComissoes.has(comissao.id)
        return `
            <tr data-id="${comissao.id}" class="${isSelected ? 'selected' : ''}">
                <td class="checkbox-col">
                    <input type="checkbox" class="comissao-checkbox" data-id="${comissao.id}" ${isSelected ? 'checked' : ''}>
                </td>
                <td>${this.escapeHtml(comissao.beneficiario_nome || 'Desconhecido')}</td>
                <td>
                    <span class="type-badge ${comissao.tipo}">
                        ${comissao.tipo === 'vendedor' ? 'Vendedor' : 'Parceiro'}
                    </span>
                </td>
                <td>${comissao.mes_referencia}</td>
                <td class="text-right">${this.formatCurrency(comissao.valor_subscricao)}</td>
                <td class="text-center">${comissao.percentagem}%</td>
                <td class="text-right money">${this.formatCurrency(comissao.valor_comissao)}</td>
                <td>
                    <span class="status-badge ${comissao.estado === 'pago' ? 'paid' : 'pending'}">
                        ${comissao.estado === 'pago' ? 'Pago' : 'Pendente'}
                    </span>
                </td>
            </tr>
        `
    }

    // =========================================================================
    // MODALS
    // =========================================================================

    private showVendedorModal(vendedor?: Vendedor): void {
        const isEdit = !!vendedor
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal">
                <div class="modal-header">
                    <h3>${isEdit ? 'Editar Vendedor' : 'Novo Vendedor'}</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <form id="vendedor-form" class="modal-body">
                    <div class="form-group">
                        <label for="vendedor-nome">Nome *</label>
                        <input type="text" id="vendedor-nome" required value="${vendedor?.nome || ''}">
                    </div>
                    <div class="form-group">
                        <label for="vendedor-email">Email *</label>
                        <input type="email" id="vendedor-email" required value="${vendedor?.email || ''}">
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label for="vendedor-telefone">Telefone</label>
                            <input type="tel" id="vendedor-telefone" value="${vendedor?.telefone || ''}">
                        </div>
                        <div class="form-group">
                            <label for="vendedor-nif">NIF</label>
                            <input type="text" id="vendedor-nif" value="${vendedor?.nif || ''}">
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="vendedor-iban">IBAN</label>
                        <input type="text" id="vendedor-iban" value="${vendedor?.iban || ''}">
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label for="vendedor-comissao">Comissão (%)</label>
                            <input type="number" id="vendedor-comissao" step="0.01" min="0" max="100" value="${vendedor?.comissao_percentagem || 10}">
                        </div>
                        ${isEdit ? `
                            <div class="form-group">
                                <label for="vendedor-ativo">Estado</label>
                                <select id="vendedor-ativo">
                                    <option value="true" ${vendedor?.ativo ? 'selected' : ''}>Ativo</option>
                                    <option value="false" ${!vendedor?.ativo ? 'selected' : ''}>Inativo</option>
                                </select>
                            </div>
                        ` : ''}
                    </div>
                    <div class="form-group">
                        <label for="vendedor-notas">Notas</label>
                        <textarea id="vendedor-notas" rows="3">${vendedor?.notas || ''}</textarea>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost modal-cancel">Cancelar</button>
                        <button type="submit" class="btn btn-primary">
                            ${isEdit ? 'Guardar' : 'Criar Vendedor'}
                        </button>
                    </div>
                </form>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.querySelector('.modal-cancel')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        const form = modal.querySelector('#vendedor-form') as HTMLFormElement
        form.addEventListener('submit', async (e) => {
            e.preventDefault()

            const data: CreateVendedorData | UpdateVendedorData = {
                nome: (document.getElementById('vendedor-nome') as HTMLInputElement).value,
                email: (document.getElementById('vendedor-email') as HTMLInputElement).value,
                telefone: (document.getElementById('vendedor-telefone') as HTMLInputElement).value || undefined,
                nif: (document.getElementById('vendedor-nif') as HTMLInputElement).value || undefined,
                iban: (document.getElementById('vendedor-iban') as HTMLInputElement).value || undefined,
                comissao_percentagem: parseFloat((document.getElementById('vendedor-comissao') as HTMLInputElement).value) || 10,
                notas: (document.getElementById('vendedor-notas') as HTMLTextAreaElement).value || undefined
            }

            if (isEdit) {
                (data as UpdateVendedorData).ativo = (document.getElementById('vendedor-ativo') as HTMLSelectElement).value === 'true'
            }

            try {
                if (isEdit && vendedor) {
                    await adminApi.updateVendedor(vendedor.id, data as UpdateVendedorData)
                    this.showToast('Vendedor atualizado com sucesso', 'success')
                } else {
                    await adminApi.createVendedor(data as CreateVendedorData)
                    this.showToast('Vendedor criado com sucesso', 'success')
                }
                closeModal()
                this.refreshCurrentTab()
            } catch (error) {
                console.error('Failed to save vendedor:', error)
                this.showToast('Erro ao guardar vendedor', 'error')
            }
        })
    }

    private showPartnerModal(partner?: PartnerResumo): void {
        const isEdit = !!partner
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal">
                <div class="modal-header">
                    <h3>${isEdit ? 'Editar Parceiro' : 'Novo Parceiro'}</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <form id="partner-form" class="modal-body">
                    <div class="form-group">
                        <label for="partner-vendedor">Vendedor</label>
                        <select id="partner-vendedor">
                            <option value="">Nenhum</option>
                            ${this.vendedores.filter(v => v.ativo).map(v => `
                                <option value="${v.id}" ${partner?.vendedor_id === v.id ? 'selected' : ''}>${this.escapeHtml(v.nome)}</option>
                            `).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="partner-nome">Nome *</label>
                        <input type="text" id="partner-nome" required value="${partner?.nome || ''}">
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label for="partner-nif">NIF</label>
                            <input type="text" id="partner-nif" value="${partner?.nif || ''}">
                        </div>
                        <div class="form-group">
                            <label for="partner-email">Email</label>
                            <input type="email" id="partner-email" value="${partner?.email || ''}">
                        </div>
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label for="partner-telefone">Telefone</label>
                            <input type="tel" id="partner-telefone" value="${partner?.telefone || ''}">
                        </div>
                        <div class="form-group">
                            <label for="partner-comissao">Comissão (%)</label>
                            <input type="number" id="partner-comissao" step="0.01" min="0" max="100" value="${partner?.comissao_percentagem || 10}">
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="partner-morada">Morada</label>
                        <input type="text" id="partner-morada" value="${partner?.morada || ''}">
                    </div>
                    <div class="form-group">
                        <label for="partner-iban">IBAN</label>
                        <input type="text" id="partner-iban" value="${partner?.iban || ''}">
                    </div>
                    ${isEdit ? `
                        <div class="form-group">
                            <label for="partner-ativo">Estado</label>
                            <select id="partner-ativo">
                                <option value="true" ${partner?.ativo ? 'selected' : ''}>Ativo</option>
                                <option value="false" ${!partner?.ativo ? 'selected' : ''}>Inativo</option>
                            </select>
                        </div>
                    ` : ''}
                    <div class="form-group">
                        <label for="partner-notas">Notas</label>
                        <textarea id="partner-notas" rows="3">${partner?.notas || ''}</textarea>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost modal-cancel">Cancelar</button>
                        <button type="submit" class="btn btn-primary">
                            ${isEdit ? 'Guardar' : 'Criar Parceiro'}
                        </button>
                    </div>
                </form>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.querySelector('.modal-cancel')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        const form = modal.querySelector('#partner-form') as HTMLFormElement
        form.addEventListener('submit', async (e) => {
            e.preventDefault()

            const vendedorId = (document.getElementById('partner-vendedor') as HTMLSelectElement).value
            const data: CreatePartnerData | UpdatePartnerData = {
                vendedor_id: vendedorId || null,  // Send null when no vendedor selected
                nome: (document.getElementById('partner-nome') as HTMLInputElement).value,
                nif: (document.getElementById('partner-nif') as HTMLInputElement).value || undefined,
                email: (document.getElementById('partner-email') as HTMLInputElement).value || undefined,
                telefone: (document.getElementById('partner-telefone') as HTMLInputElement).value || undefined,
                morada: (document.getElementById('partner-morada') as HTMLInputElement).value || undefined,
                iban: (document.getElementById('partner-iban') as HTMLInputElement).value || undefined,
                comissao_percentagem: parseFloat((document.getElementById('partner-comissao') as HTMLInputElement).value) || 10,
                notas: (document.getElementById('partner-notas') as HTMLTextAreaElement).value || undefined
            }

            if (isEdit) {
                (data as UpdatePartnerData).ativo = (document.getElementById('partner-ativo') as HTMLSelectElement).value === 'true'
            }

            try {
                if (isEdit && partner) {
                    await adminApi.updatePartner(partner.id, data as UpdatePartnerData)
                    this.showToast('Parceiro atualizado com sucesso', 'success')
                } else {
                    await adminApi.createPartner(data as CreatePartnerData)
                    this.showToast('Parceiro criado com sucesso', 'success')
                }
                closeModal()
                this.refreshCurrentTab()
            } catch (error) {
                console.error('Failed to save partner:', error)
                this.showToast('Erro ao guardar parceiro', 'error')
            }
        })
    }

    private showAssignVendedorModal(cliente: ClienteInfo): void {
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal modal-sm">
                <div class="modal-header">
                    <h3>Atribuir Vendedor</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="cliente-info">
                        <p><strong>Cliente:</strong> ${this.escapeHtml(cliente.company_name || cliente.name)}</p>
                        <p><strong>NIF:</strong> ${this.escapeHtml(cliente.nif)}</p>
                        ${cliente.email ? `<p><strong>Email:</strong> ${this.escapeHtml(cliente.email)}</p>` : ''}
                    </div>
                    
                    <div class="assign-note">
                        <i class="fa-solid fa-info-circle"></i>
                        <p>Ao atribuir um vendedor a este cliente direto, futuras comissões serão calculadas para o vendedor selecionado.</p>
                    </div>
                    
                    <div class="form-group">
                        <label for="assign-vendedor">Vendedor</label>
                        <select id="assign-vendedor">
                            <option value="">Nenhum (remover atribuição)</option>
                            ${this.vendedores.filter(v => v.ativo).map(v => `
                                <option value="${v.id}" ${cliente.vendedor_id === v.id ? 'selected' : ''}>${this.escapeHtml(v.nome)}</option>
                            `).join('')}
                        </select>
                    </div>
                    
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost modal-cancel">Cancelar</button>
                        <button type="button" class="btn btn-primary" id="confirm-assign">
                            Confirmar
                        </button>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.querySelector('.modal-cancel')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        modal.querySelector('#confirm-assign')?.addEventListener('click', async () => {
            const vendedorId = (document.getElementById('assign-vendedor') as HTMLSelectElement).value

            try {
                await adminApi.assignVendedor(cliente.user_id, {
                    vendedor_id: vendedorId || null
                })
                this.showToast(
                    vendedorId ? 'Vendedor atribuído com sucesso' : 'Atribuição removida com sucesso',
                    'success'
                )
                closeModal()
                this.refreshCurrentTab()
            } catch (error) {
                console.error('Failed to assign vendedor:', error)
                this.showToast('Erro ao atribuir vendedor', 'error')
            }
        })
    }

    private showMarkAsPaidModal(): void {
        const selectedIds = Array.from(this.selectedComissoes)
        const selectedComissoes = this.comissoes.filter(c => selectedIds.includes(c.id))
        const total = selectedComissoes.reduce((sum, c) => sum + c.valor_comissao, 0)

        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal modal-sm">
                <div class="modal-header">
                    <h3>Marcar como Pago</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="payment-summary">
                        <div class="payment-count">
                            <i class="fa-solid fa-receipt"></i>
                            <span>${selectedIds.length} comissões selecionadas</span>
                        </div>
                        <div class="payment-total-value">
                            <span class="label">Total:</span>
                            <span class="value">${this.formatCurrency(total)}</span>
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label for="payment-date">Data de Pagamento *</label>
                        <input type="date" id="payment-date" required value="${new Date().toISOString().split('T')[0]}">
                    </div>
                    
                    <div class="form-group">
                        <label for="payment-ref">Referência</label>
                        <input type="text" id="payment-ref" placeholder="Ex: Transferência bancária #12345">
                    </div>
                    
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost modal-cancel">Cancelar</button>
                        <button type="button" class="btn btn-success" id="confirm-payment">
                            <i class="fa-solid fa-check"></i>
                            Confirmar Pagamento
                        </button>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.querySelector('.modal-cancel')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        modal.querySelector('#confirm-payment')?.addEventListener('click', async () => {
            const data_pagamento = (document.getElementById('payment-date') as HTMLInputElement).value
            const referencia_pagamento = (document.getElementById('payment-ref') as HTMLInputElement).value

            if (!data_pagamento) {
                this.showToast('Por favor, selecione a data de pagamento', 'error')
                return
            }

            try {
                await adminApi.markComissoesPaid({
                    ids: selectedIds,
                    data_pagamento,
                    referencia_pagamento: referencia_pagamento || undefined
                })
                this.showToast(`${selectedIds.length} comissões marcadas como pagas`, 'success')
                this.selectedComissoes.clear()
                closeModal()
                this.refreshCurrentTab()
            } catch (error) {
                console.error('Failed to mark as paid:', error)
                this.showToast('Erro ao marcar comissões como pagas', 'error')
            }
        })
    }

    private async showVendedorPartnersModal(vendedor: VendedorResumo): Promise<void> {
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal modal-lg">
                <div class="modal-header">
                    <h3><i class="fa-solid fa-handshake"></i> Partners de ${this.escapeHtml(vendedor.nome)}</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="loading-state">
                        <div class="loading-spinner"></div>
                        <p>A carregar partners...</p>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        // Load partners
        try {
            const partners = await adminApi.getVendedorPartners(vendedor.id)
            const modalBody = modal.querySelector('.modal-body')
            if (modalBody) {
                if (partners.length === 0) {
                    modalBody.innerHTML = `
                        <div class="empty-state">
                            <i class="fa-solid fa-inbox empty-icon"></i>
                            <h3>Sem Partners</h3>
                            <p>Este vendedor ainda não tem partners associados.</p>
                        </div>
                    `
                } else {
                    modalBody.innerHTML = `
                        <div class="partners-list-modal">
                            <p class="list-count"><strong>${partners.length}</strong> partner${partners.length !== 1 ? 's' : ''} encontrado${partners.length !== 1 ? 's' : ''}</p>
                            <div class="admin-table-container">
                                <table class="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Nome</th>
                                            <th>Email</th>
                                            <th>NIF</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${partners.map(p => `
                                            <tr>
                                                <td>
                                                    <div class="entity-name">
                                                        <i class="fa-solid fa-handshake entity-icon"></i>
                                                        <span>${this.escapeHtml(p.nome)}</span>
                                                    </div>
                                                </td>
                                                <td>${p.email ? this.escapeHtml(p.email) : '<span class="text-muted">N/A</span>'}</td>
                                                <td>${p.nif ? this.escapeHtml(p.nif) : '<span class="text-muted">N/A</span>'}</td>
                                                <td>
                                                    <span class="status-badge ${p.ativo ? 'active' : 'inactive'}">
                                                        ${p.ativo ? 'Ativo' : 'Inativo'}
                                                    </span>
                                                </td>
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    `
                }
            }
        } catch (error) {
            console.error('Failed to load vendedor partners:', error)
            const modalBody = modal.querySelector('.modal-body')
            if (modalBody) {
                modalBody.innerHTML = `
                    <div class="error-state">
                        <i class="fa-solid fa-exclamation-circle"></i>
                        <p>Erro ao carregar partners. Por favor, tente novamente.</p>
                    </div>
                `
            }
        }
    }

    private async showPartnerClientesModal(partner: PartnerResumo): Promise<void> {
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal modal-lg">
                <div class="modal-header">
                    <h3><i class="fa-solid fa-users"></i> Clientes de ${this.escapeHtml(partner.nome)}</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="loading-state">
                        <div class="loading-spinner"></div>
                        <p>A carregar clientes...</p>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        // Load clientes
        try {
            const clientes = await adminApi.getPartnerClientes(partner.id)
            const modalBody = modal.querySelector('.modal-body')
            if (modalBody) {
                if (clientes.length === 0) {
                    modalBody.innerHTML = `
                        <div class="empty-state">
                            <i class="fa-solid fa-inbox empty-icon"></i>
                            <h3>Sem Clientes</h3>
                            <p>Este parceiro ainda não tem clientes associados.</p>
                        </div>
                    `
                } else {
                    modalBody.innerHTML = `
                        <div class="clientes-list-modal">
                            <p class="list-count"><strong>${clientes.length}</strong> cliente${clientes.length !== 1 ? 's' : ''} encontrado${clientes.length !== 1 ? 's' : ''}</p>
                            <div class="admin-table-container">
                                <table class="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Empresa</th>
                                            <th>Email</th>
                                            <th>NIF</th>
                                            <th>Plano</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${clientes.map(c => `
                                            <tr>
                                                <td>
                                                    <div class="entity-name">
                                                        <i class="fa-solid fa-building entity-icon"></i>
                                                        <span>${this.escapeHtml(c.company_name || c.name)}</span>
                                                    </div>
                                                </td>
                                                <td>${c.email ? this.escapeHtml(c.email) : '<span class="text-muted">N/A</span>'}</td>
                                                <td>${this.escapeHtml(c.nif)}</td>
                                                <td>
                                                    <span class="plan-badge ${this.getSubscriptionStatusClass(c.subscription_status)}">
                                                        ${this.getPlanLabel(c.plan_id)}
                                                    </span>
                                                </td>
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    `
                }
            }
        } catch (error) {
            console.error('Failed to load partner clientes:', error)
            const modalBody = modal.querySelector('.modal-body')
            if (modalBody) {
                modalBody.innerHTML = `
                    <div class="error-state">
                        <i class="fa-solid fa-exclamation-circle"></i>
                        <p>Erro ao carregar clientes. Por favor, tente novamente.</p>
                    </div>
                `
            }
        }
    }

    private showDeleteConfirmation(type: 'vendedor' | 'partner', id: string, name: string): void {
        const modal = document.createElement('div')
        modal.className = 'modal-overlay'
        modal.innerHTML = `
            <div class="modal admin-modal modal-sm">
                <div class="modal-header warning">
                    <h3><i class="fa-solid fa-triangle-exclamation"></i> Confirmar Desativação</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <p>Tem a certeza que deseja desativar ${type === 'vendedor' ? 'o vendedor' : 'o parceiro'} <strong>${this.escapeHtml(name)}</strong>?</p>
                    <p class="text-muted">Esta ação irá desativar o registo mas não o eliminará permanentemente.</p>
                    
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost modal-cancel">Cancelar</button>
                        <button type="button" class="btn btn-danger" id="confirm-delete">
                            <i class="fa-solid fa-trash"></i>
                            Desativar
                        </button>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event handlers
        const closeModal = () => modal.remove()
        modal.querySelector('.modal-close')?.addEventListener('click', closeModal)
        modal.querySelector('.modal-cancel')?.addEventListener('click', closeModal)
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        modal.querySelector('#confirm-delete')?.addEventListener('click', async () => {
            try {
                if (type === 'vendedor') {
                    await adminApi.deleteVendedor(id)
                } else {
                    await adminApi.deletePartner(id)
                }
                this.showToast(`${type === 'vendedor' ? 'Vendedor' : 'Parceiro'} desativado com sucesso`, 'success')
                closeModal()
                this.refreshCurrentTab()
            } catch (error) {
                console.error(`Failed to delete ${type}:`, error)
                this.showToast(`Erro ao desativar ${type}`, 'error')
            }
        })
    }

    // =========================================================================
    // EVENT HANDLERS
    // =========================================================================

    private addEventListeners(container: HTMLElement): void {
        // Back button
        container.querySelector('#admin-back')?.addEventListener('click', () => {
            router.navigate('/dashboard')
        })

        // Refresh button
        container.querySelector('#admin-refresh')?.addEventListener('click', async (e) => {
            const btn = e.currentTarget as HTMLButtonElement
            const icon = btn?.querySelector('i')

            // Add spinning animation
            if (icon) {
                icon.classList.add('fa-spin')
            }
            if (btn) {
                btn.disabled = true
            }

            try {
                await this.refreshCurrentTab()
                this.showToast('Dados atualizados com sucesso', 'success')
            } catch (error) {
                console.error('Failed to refresh data:', error)
                this.showToast('Erro ao atualizar dados', 'error')
            } finally {
                // Remove spinning animation
                if (icon) {
                    icon.classList.remove('fa-spin')
                }
                if (btn) {
                    btn.disabled = false
                }
            }
        })

        // Tab navigation
        container.querySelectorAll('.admin-tab').forEach(tab => {
            tab.addEventListener('click', async (e) => {
                const newTab = (e.currentTarget as HTMLElement).dataset.tab as AdminTab
                if (newTab && newTab !== this.currentTab) {
                    this.currentTab = newTab
                    await this.loadData()
                    const content = container.querySelector('.admin-content')
                    if (content) {
                        content.innerHTML = this.renderTabContent()
                        this.updateTabStyles(container)
                        this.addContentEventListeners(container)
                    }
                }
            })
        })

        this.addContentEventListeners(container)
    }

    private addContentEventListeners(container: HTMLElement): void {
        // Vendedores
        container.querySelector('#add-vendedor')?.addEventListener('click', () => {
            this.showVendedorModal()
        })

        container.querySelectorAll('[data-action="edit-vendedor"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                try {
                    const vendedor = await adminApi.getVendedor(id)
                    this.showVendedorModal(vendedor)
                } catch (error) {
                    console.error('Failed to load vendedor:', error)
                    this.showToast('Erro ao carregar vendedor', 'error')
                }
            })
        })

        container.querySelectorAll('[data-action="delete-vendedor"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                const vendedor = this.vendedores.find(v => v.id === id)
                if (vendedor) {
                    this.showDeleteConfirmation('vendedor', id, vendedor.nome)
                }
            })
        })

        container.querySelectorAll('[data-action="view-vendedor-partners"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                const vendedor = this.vendedores.find(v => v.id === id)
                if (vendedor) {
                    await this.showVendedorPartnersModal(vendedor)
                }
            })
        })

        // Partners
        container.querySelector('#add-partner')?.addEventListener('click', () => {
            this.showPartnerModal()
        })

        container.querySelectorAll('[data-action="edit-partner"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                const partner = this.partners.find(p => p.id === id)
                if (partner) {
                    this.showPartnerModal(partner)
                }
            })
        })

        container.querySelectorAll('[data-action="delete-partner"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                const partner = this.partners.find(p => p.id === id)
                if (partner) {
                    this.showDeleteConfirmation('partner', id, partner.nome)
                }
            })
        })

        container.querySelectorAll('[data-action="view-partner-clientes"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = (e.currentTarget as HTMLElement).dataset.id!
                const partner = this.partners.find(p => p.id === id)
                if (partner) {
                    await this.showPartnerClientesModal(partner)
                }
            })
        })

        // Clientes - Assign Vendedor
        container.querySelectorAll('[data-action="assign-vendedor"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const id = parseInt((e.currentTarget as HTMLElement).dataset.id!)
                const cliente = this.clientes.find(c => c.user_id === id)
                if (cliente) {
                    this.showAssignVendedorModal(cliente)
                }
            })
        })

        // Todos Clientes Plan Filter
        container.querySelector('#apply-cliente-filters')?.addEventListener('click', async () => {
            this.clienteFilter = {
                plan: (document.getElementById('filter-plan') as HTMLSelectElement)?.value || ''
            }
            await this.loadData()
            const content = container.querySelector('.admin-content')
            if (content) {
                content.innerHTML = this.renderTabContent()
                this.addContentEventListeners(container)
            }
        })

        // Comissoes
        container.querySelector('#apply-filters')?.addEventListener('click', async () => {
            this.comissaoFilter = {
                mes: (document.getElementById('filter-mes') as HTMLInputElement)?.value || '',
                tipo: (document.getElementById('filter-tipo') as HTMLSelectElement)?.value || '',
                estado: (document.getElementById('filter-estado') as HTMLSelectElement)?.value || ''
            }
            await this.loadData()
            const content = container.querySelector('.admin-content')
            if (content) {
                content.innerHTML = this.renderTabContent()
                this.addContentEventListeners(container)
            }
        })

        container.querySelector('#mark-paid')?.addEventListener('click', () => {
            if (this.selectedComissoes.size > 0) {
                this.showMarkAsPaidModal()
            }
        })

        container.querySelector('#export-comissoes')?.addEventListener('click', async () => {
            try {
                const data = await adminApi.exportComissoes(this.comissaoFilter)
                // Convert to CSV and download
                this.downloadCSV(data.comissoes)
                this.showToast('Exportação concluída', 'success')
            } catch (error) {
                console.error('Failed to export:', error)
                this.showToast('Erro ao exportar comissões', 'error')
            }
        })

        // Comissao checkboxes
        container.querySelector('#select-all-comissoes')?.addEventListener('change', (e) => {
            const checked = (e.target as HTMLInputElement).checked
            container.querySelectorAll('.comissao-checkbox').forEach(cb => {
                const checkbox = cb as HTMLInputElement
                checkbox.checked = checked
                const id = checkbox.dataset.id!
                if (checked) {
                    this.selectedComissoes.add(id)
                } else {
                    this.selectedComissoes.delete(id)
                }
            })
            this.updateSelectedCount(container)
        })

        container.querySelectorAll('.comissao-checkbox').forEach(cb => {
            cb.addEventListener('change', (e) => {
                const checkbox = e.target as HTMLInputElement
                const id = checkbox.dataset.id!
                if (checkbox.checked) {
                    this.selectedComissoes.add(id)
                } else {
                    this.selectedComissoes.delete(id)
                }
                this.updateSelectedCount(container)
            })
        })
    }

    private updateTabStyles(container: HTMLElement): void {
        container.querySelectorAll('.admin-tab').forEach(tab => {
            tab.classList.remove('active')
            if ((tab as HTMLElement).dataset.tab === this.currentTab) {
                tab.classList.add('active')
            }
        })
    }

    private updateSelectedCount(container: HTMLElement): void {
        const countEl = container.querySelector('#selected-count')
        if (countEl) {
            countEl.textContent = `${this.selectedComissoes.size} selecionadas`
        }

        const markPaidBtn = container.querySelector('#mark-paid') as HTMLButtonElement
        if (markPaidBtn) {
            markPaidBtn.disabled = this.selectedComissoes.size === 0
        }
    }

    private async refreshCurrentTab(): Promise<void> {
        await this.loadData()
        const container = document.querySelector('.admin-page')
        if (container) {
            const content = container.querySelector('.admin-content')
            if (content) {
                content.innerHTML = this.renderTabContent()
                this.addContentEventListeners(container as HTMLElement)
            }
        }
    }

    // =========================================================================
    // UTILITIES
    // =========================================================================

    private renderEmptyState(type: string): string {
        return `
            <div class="empty-state">
                <i class="fa-solid fa-inbox empty-icon"></i>
                <h3>Sem ${type}</h3>
                <p>Não existem ${type} para mostrar.</p>
            </div>
        `
    }

    private formatCurrency(value: number): string {
        return new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: 'EUR'
        }).format(value)
    }

    private getPlanLabel(planId: string | null): string {
        if (!planId) return 'Gratuito'

        // Check for friendly plan names first
        const planMap: Record<string, string> = {
            'solo': 'Solo',
            'empresas': 'Empresas',
            'premium': 'Ultra Premium',
            'iniciante': 'Solo', // Legacy name mapping
            'free': 'Gratuito'
        }

        // If it's a simple plan name, return the mapped label
        const lowerPlanId = planId.toLowerCase()
        if (planMap[lowerPlanId]) {
            return planMap[lowerPlanId]
        }

        // If it's a Stripe price ID (starts with "price_"), try to determine the plan type
        // This handles cases where the database stores the raw Stripe price ID
        if (planId.startsWith('price_')) {
            // Try to fetch plan configuration from stripe config endpoint (cached)
            // For now, return a generic "Subscrito" (Subscribed) label
            // The actual plan name will be visible in other contexts
            return 'Subscrito'
        }

        // Fallback: return the raw ID (shouldn't happen often)
        return planId
    }

    private getSubscriptionStatusClass(status: string | null): string {
        if (!status) return 'free'
        const statusMap: Record<string, string> = {
            'active': 'active',
            'trial': 'trial',
            'past_due': 'warning',
            'canceled': 'canceled',
            'free': 'free'
        }
        return statusMap[status] || 'free'
    }

    private escapeHtml(unsafe: string): string {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;")
    }

    private showToast(message: string, type: 'success' | 'error'): void {
        const toast = document.createElement('div')
        toast.className = `toast toast-${type}`
        toast.innerHTML = `
            <i class="fa-solid ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}"></i>
            <span>${message}</span>
        `

        document.body.appendChild(toast)

        // Trigger animation
        requestAnimationFrame(() => {
            toast.classList.add('show')
        })

        setTimeout(() => {
            toast.classList.remove('show')
            setTimeout(() => toast.remove(), 300)
        }, 3000)
    }

    private downloadCSV(comissoes: Comissao[]): void {
        const headers = ['ID', 'Tipo', 'Beneficiário', 'Mês', 'Valor Subscrição', 'Percentagem', 'Valor Comissão', 'Estado', 'Data Pagamento', 'Referência']
        const rows = comissoes.map(c => [
            c.id,
            c.tipo,
            c.beneficiario_nome || 'Desconhecido',
            c.mes_referencia,
            c.valor_subscricao.toFixed(2),
            c.percentagem.toString(),
            c.valor_comissao.toFixed(2),
            c.estado,
            c.data_pagamento || '',
            c.referencia_pagamento || ''
        ])

        const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n')
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `comissoes_${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }
}
